import { useState } from 'react';
import {CheckCircle2} from 'lucide-react';
import { lumi } from '@/lib/lumi';

// ──────────────────────────────────────────
// Tipos
// ──────────────────────────────────────────
type FormStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 'done';

interface FormData {
  nome: string;
  whatsapp: string;
  email: string;
  areasBloqueios: string[];
  processosAnteriores: string;
  motivacao: string;
  tempoSituacao: string;
  esperaTransformar: string;
  disponibilidadeProcesso: string;
  condicaoFinanceira: string;
}

// ──────────────────────────────────────────
// Constantes
// ──────────────────────────────────────────
const AREAS = [
  'Financeira e profissional',
  'Relacionamentos',
  'Saúde',
  'Autoestima e merecimento',
  'Propósito',
  'Todas as áreas acima',
];

const COUNTRY_CODES = [
  { code: '+55',   flag: '🇧🇷', name: 'Brasil' },
  { code: '+1',    flag: '🇺🇸', name: 'Estados Unidos' },
  { code: '+351',  flag: '🇵🇹', name: 'Portugal' },
  { code: 'outro', flag: '🌍',  name: 'Outro país' },
];

const DISPONIBILIDADE_OPTIONS = [
  'Sim, estou disponível para o processo de 30 dias',
  'Preciso entender melhor como funciona',
  'Ainda não consigo me comprometer com 30 dias',
];

const CONDICAO_FINANCEIRA_OPTIONS = [
  'Sim, se fizer sentido estou pronta para investir',
  'Preciso entender melhor o investimento',
  'Ainda não é o momento financeiro ideal',
];

// ──────────────────────────────────────────
// Componente
// ──────────────────────────────────────────
export default function LeituraInscricao() {
  const [formStep, setFormStep] = useState<FormStep>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryCode, setCountryCode] = useState('+55');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [outroCodigoPais, setOutroCodigoPais] = useState('');
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    whatsapp: '',
    email: '',
    areasBloqueios: [],
    processosAnteriores: '',
    motivacao: '',
    tempoSituacao: '',
    esperaTransformar: '',
    disponibilidadeProcesso: '',
    condicaoFinanceira: '',
  });

  // ── Toggle área de bloqueio ──
  const toggleArea = (area: string) => {
    const current = formData.areasBloqueios;
    if (current.includes(area)) {
      setFormData({ ...formData, areasBloqueios: current.filter(a => a !== area) });
    } else if (current.length < 2) {
      setFormData({ ...formData, areasBloqueios: [...current, area] });
    }
  };

  // ── Avançar etapa ──
  const nextStep = () => {
    if (formStep === 1) setFormStep(2);
    else if (formStep === 2) setFormStep(3);
    else if (formStep === 3) setFormStep(4);
    else if (formStep === 4) setFormStep(5);
    else if (formStep === 5) setFormStep(6);
    else if (formStep === 6) setFormStep(7);
    else if (formStep === 7) setFormStep(8);
    else if (formStep === 8) setFormStep(9);
    else if (formStep === 9) setFormStep(10);
  };

  // ── Submissão ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const codigoFinal = countryCode === 'outro' ? outroCodigoPais.trim() : countryCode;
      const fullWhatsapp = `${codigoFinal} ${whatsappNumber}`;
      const areasBloqueiosTexto = formData.areasBloqueios.join(', ');
      await lumi.entities.leitura_inscricao_submissions.create({
        nome: formData.nome,
        whatsapp: fullWhatsapp,
        email: formData.email,
        areasBloqueios: areasBloqueiosTexto,
        processosAnteriores: formData.processosAnteriores,
        motivacao: formData.motivacao,
        tempoSituacao: formData.tempoSituacao,
        esperaTransformar: formData.esperaTransformar,
        disponibilidadeProcesso: formData.disponibilidadeProcesso,
        condicaoFinanceira: formData.condicaoFinanceira,
        status: 'Pendente',
        createdAt: now,
        updatedAt: now,
      });
      // Notificação por e-mail para monicamrsales@gmail.com (não bloqueia o fluxo do usuário em caso de falha)
      fetch(`https://api.lumi.new/v1/functions/${import.meta.env.VITE_LUMI_PROJECT_ID || 'p418823699841495040'}/notifyLeituraSubmission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formData.nome,
          email: formData.email,
          whatsapp: fullWhatsapp,
          areasBloqueios: areasBloqueiosTexto,
          processosAnteriores: formData.processosAnteriores,
          motivacao: formData.motivacao,
          tempoSituacao: formData.tempoSituacao,
          esperaTransformar: formData.esperaTransformar,
          disponibilidadeProcesso: formData.disponibilidadeProcesso,
          condicaoFinanceira: formData.condicaoFinanceira,
          createdAt: now,
        }),
      }).catch(() => { /* silencioso: não interrompe o fluxo do formulário */ });
      setFormStep('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      alert('Erro ao enviar. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Barra de progresso ──
  const progressSteps = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const currentStepNum = typeof formStep === 'number' ? formStep : 0;
  const progressPct = currentStepNum > 0 ? Math.round((currentStepNum / 10) * 100) : 0;

  return (
    <div style={{ background: '#0D0A12', minHeight: '100vh', color: '#FAFCF8' }}>
      {/* Cabeçalho minimalista */}
      <header style={{ borderBottom: '1px solid rgba(184,141,86,0.12)', padding: '1.5rem' }}>
        <div className="max-w-xl mx-auto text-center">
          <p style={{ color: '#B88D56', fontFamily: 'Lora, Georgia, serif', letterSpacing: '0.18em', fontSize: '0.8rem', textTransform: 'uppercase' }}>
            Reacenda Luz
          </p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 600, fontSize: '1.6rem', marginTop: '0.5rem' }}>
            Leitura do Campo Vibracional
          </h1>
        </div>
      </header>

      <section className="max-w-xl mx-auto px-6 py-12 md:py-16">

        {/* ── FORMULÁRIO QUALIFICADO ── */}
        {typeof formStep === 'number' && formStep >= 1 && formStep <= 10 && (
          <form onSubmit={formStep === 10 ? handleSubmit : (e) => { e.preventDefault(); nextStep(); }} className="animate-fadeIn">
            {/* Barra de progresso */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.8rem' }}>Passo {currentStepNum} de 10</span>
                <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.8rem' }}>{progressPct}%</span>
              </div>
              <div style={{ height: 3, background: 'rgba(184,141,86,0.2)', borderRadius: 99 }}>
                <div style={{ height: '100%', width: `${progressPct}%`, background: '#C8923A', borderRadius: 99, transition: 'width 0.4s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem' }}>
                {progressSteps.map(s => (
                  <div key={s} style={{ width: 8, height: 8, borderRadius: '50%', background: s <= currentStepNum ? '#C8923A' : 'rgba(184,141,86,0.25)', transition: 'background 0.3s' }} />
                ))}
              </div>
            </div>

            {/* Step 1 — Nome */}
            {formStep === 1 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Qual é o seu nome completo? *</label>
                <input
                  type="text" required autoFocus
                  value={formData.nome}
                  onChange={e => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Seu nome completo"
                  style={inputStyle}
                />
              </div>
            )}

            {/* Step 2 — WhatsApp */}
            {formStep === 2 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Qual é o seu WhatsApp com código do país? *</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <select
                    value={countryCode}
                    onChange={e => { setCountryCode(e.target.value); setOutroCodigoPais(''); }}
                    style={{ ...inputStyle, width: 'auto', flexShrink: 0, paddingRight: '0.75rem', cursor: 'pointer', backgroundColor: '#1A1424', colorScheme: 'dark' }}
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.name} {c.code !== 'outro' ? c.code : ''}
                      </option>
                    ))}
                  </select>
                  {countryCode === 'outro' && (
                    <input
                      type="text"
                      required
                      value={outroCodigoPais}
                      onChange={e => setOutroCodigoPais(e.target.value)}
                      placeholder="Ex: +44"
                      style={{ ...inputStyle, width: 90, flexShrink: 0 }}
                    />
                  )}
                  <input
                    type="tel" required autoFocus
                    value={whatsappNumber}
                    onChange={e => setWhatsappNumber(e.target.value)}
                    placeholder="(11) 99999-9999"
                    style={{ ...inputStyle, flex: 1, minWidth: 140 }}
                  />
                </div>
              </div>
            )}

            {/* Step 3 — Email */}
            {formStep === 3 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Qual é o seu email? *</label>
                <input
                  type="email" required autoFocus
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu@email.com"
                  style={inputStyle}
                />
              </div>
            )}

            {/* Step 4 — Áreas (múltipla escolha até 2) */}
            {formStep === 4 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Qual área da sua vida está com mais bloqueios agora? (escolha até 2) *</label>
                <div className="space-y-3 mt-3">
                  {AREAS.map(area => {
                    const selected = formData.areasBloqueios.includes(area);
                    const disabled = !selected && formData.areasBloqueios.length >= 2;
                    return (
                      <button
                        key={area}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleArea(area)}
                        style={{
                          width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: selected ? 'rgba(200,146,58,0.18)' : 'rgba(184,141,86,0.05)',
                          border: `1px solid ${selected ? '#C8923A' : 'rgba(184,141,86,0.2)'}`,
                          borderRadius: 4, padding: '0.9rem 1.1rem', cursor: disabled ? 'default' : 'pointer',
                          opacity: disabled ? 0.4 : 1, transition: 'all 0.2s',
                        }}
                      >
                        {selected
                          ? <CheckCircle2 style={{ width: 18, height: 18, color: '#C8923A', flexShrink: 0 }} />
                          : <span style={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid rgba(184,141,86,0.45)', display: 'inline-block', flexShrink: 0 }} />
                        }
                        <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1rem' }}>{area}</span>
                      </button>
                    );
                  })}
                </div>
                {formData.areasBloqueios.length === 0 && (
                  <p style={{ color: '#B88D56', fontSize: '0.85rem', marginTop: '0.75rem', fontFamily: 'Lora, Georgia, serif' }}>Selecione pelo menos 1 área.</p>
                )}
              </div>
            )}

            {/* Step 5 — Processos anteriores */}
            {formStep === 5 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Você já fez algum processo de terapia, cura ou autoconhecimento antes? O que funcionou e o que ainda não mudou? *</label>
                <textarea
                  required autoFocus rows={5}
                  value={formData.processosAnteriores}
                  onChange={e => setFormData({ ...formData, processosAnteriores: e.target.value })}
                  placeholder="Descreva com honestidade..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                />
              </div>
            )}

            {/* Step 6 — Motivação */}
            {formStep === 6 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>O que te trouxe até aqui? O que está acontecendo na sua vida agora que fez você buscar esse processo? *</label>
                <textarea
                  required autoFocus rows={5}
                  value={formData.motivacao}
                  onChange={e => setFormData({ ...formData, motivacao: e.target.value })}
                  placeholder="Compartilhe com honestidade..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                />
              </div>
            )}

            {/* Step 7 — Tempo nessa situação */}
            {formStep === 7 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Há quanto tempo você sente que está nessa situação? *</label>
                <textarea
                  required autoFocus rows={5}
                  value={formData.tempoSituacao}
                  onChange={e => setFormData({ ...formData, tempoSituacao: e.target.value })}
                  placeholder="Descreva com honestidade..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                />
              </div>
            )}

            {/* Step 8 — O que espera transformar */}
            {formStep === 8 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>O que você espera transformar com esse processo? *</label>
                <textarea
                  required autoFocus rows={5}
                  value={formData.esperaTransformar}
                  onChange={e => setFormData({ ...formData, esperaTransformar: e.target.value })}
                  placeholder="Compartilhe com honestidade..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                />
              </div>
            )}

            {/* Step 9 — Disponibilidade para 30 dias */}
            {formStep === 9 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Você está disponível para o processo de 30 dias? *</label>
                <div className="space-y-3 mt-3">
                  {DISPONIBILIDADE_OPTIONS.map(option => {
                    const selected = formData.disponibilidadeProcesso === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData({ ...formData, disponibilidadeProcesso: option })}
                        style={{
                          width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: selected ? 'rgba(200,146,58,0.18)' : 'rgba(184,141,86,0.05)',
                          border: `1px solid ${selected ? '#C8923A' : 'rgba(184,141,86,0.2)'}`,
                          borderRadius: 4, padding: '0.9rem 1.1rem', cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {selected
                          ? <CheckCircle2 style={{ width: 18, height: 18, color: '#C8923A', flexShrink: 0 }} />
                          : <span style={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid rgba(184,141,86,0.45)', display: 'inline-block', flexShrink: 0 }} />
                        }
                        <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1rem' }}>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 10 — Condição financeira */}
            {formStep === 10 && (
              <div className="animate-fadeIn">
                <label style={labelStyle}>Qual é a sua condição financeira para investir neste processo? *</label>
                <div className="space-y-3 mt-3">
                  {CONDICAO_FINANCEIRA_OPTIONS.map(option => {
                    const selected = formData.condicaoFinanceira === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setFormData({ ...formData, condicaoFinanceira: option })}
                        style={{
                          width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: selected ? 'rgba(200,146,58,0.18)' : 'rgba(184,141,86,0.05)',
                          border: `1px solid ${selected ? '#C8923A' : 'rgba(184,141,86,0.2)'}`,
                          borderRadius: 4, padding: '0.9rem 1.1rem', cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        {selected
                          ? <CheckCircle2 style={{ width: 18, height: 18, color: '#C8923A', flexShrink: 0 }} />
                          : <span style={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid rgba(184,141,86,0.45)', display: 'inline-block', flexShrink: 0 }} />
                        }
                        <span style={{ fontFamily: 'Lora, Georgia, serif', color: '#FAFCF8', fontSize: '1rem' }}>{option}</span>
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.5)', fontSize: '0.85rem', marginTop: '1rem', lineHeight: 1.7, textAlign: 'center' }}>
                  Seus dados são confidenciais e não serão compartilhados.
                </p>
              </div>
            )}

            {/* Botão avançar / enviar */}
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  (formStep === 1 && !formData.nome.trim()) ||
                  (formStep === 2 && (!whatsappNumber.trim() || (countryCode === 'outro' && !outroCodigoPais.trim()))) ||
                  (formStep === 3 && !formData.email.trim()) ||
                  (formStep === 4 && formData.areasBloqueios.length === 0) ||
                  (formStep === 5 && !formData.processosAnteriores.trim()) ||
                  (formStep === 6 && !formData.motivacao.trim()) ||
                  (formStep === 7 && !formData.tempoSituacao.trim()) ||
                  (formStep === 8 && !formData.esperaTransformar.trim()) ||
                  (formStep === 9 && !formData.disponibilidadeProcesso.trim()) ||
                  (formStep === 10 && !formData.condicaoFinanceira.trim())
                }
                style={{
                  background: '#C8923A',
                  color: '#FAFCF8',
                  fontFamily: 'Lora, Georgia, serif',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '16px 36px',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  opacity: isSubmitting ? 0.6 : 1,
                  transition: 'background 0.2s',
                }}
              >
                {isSubmitting ? 'Enviando...' : formStep === 10 ? 'Enviar minhas respostas' : 'Continuar →'}
              </button>
            </div>
          </form>
        )}

        {/* ── CONFIRMAÇÃO ── */}
        {formStep === 'done' && (
          <div className="animate-fadeIn text-center">
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(184,141,86,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <CheckCircle2 style={{ width: 28, height: 28, color: '#B88D56' }} />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 500, marginBottom: '1.5rem' }}>
              Suas respostas chegaram.
            </h2>
            <div style={{ border: '1px solid rgba(184,141,86,0.25)', borderRadius: 6, padding: '1.75rem 2rem', marginBottom: '1.5rem', textAlign: 'left' }}>
              <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(250,252,248,0.9)', fontSize: '1.05rem', lineHeight: 1.9, marginBottom: '1rem' }}>
                Obrigada por compartilhar com tanta honestidade. Lerei tudo com cuidado e presença e entrarei em contato em até 48 horas para conversarmos.
              </p>
              <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#EFC98A', fontSize: '1.1rem', fontStyle: 'italic' }}>
                Com amor e presença, Mônica — Reacenda Luz
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Rodapé */}
      <footer style={{ borderTop: '1px solid rgba(184,141,86,0.1)', padding: '2rem 1.5rem', textAlign: 'center' }}>
        <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(184,141,86,0.5)', fontSize: '0.85rem' }}>
          © Reacenda Luz · Todos os direitos reservados
        </p>
      </footer>
    </div>
  );
}

// ──────────────────────────────────────────
// Estilos inline reutilizáveis
// ──────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Lora, Georgia, serif',
  color: '#FAFCF8',
  fontSize: '1.05rem',
  lineHeight: 1.7,
  marginBottom: '1rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(184,141,86,0.06)',
  border: '1px solid rgba(184,141,86,0.3)',
  borderRadius: 4,
  padding: '14px 16px',
  fontFamily: 'Lora, Georgia, serif',
  color: '#FAFCF8',
  fontSize: '1rem',
  outline: 'none',
  boxSizing: 'border-box',
};
